import { faChevronLeft } from "@jewlr/pro-light-svg-icons/faChevronLeft"
import { faTimes } from "@jewlr/pro-light-svg-icons/faTimes"
import { FocusScope } from "@react-aria/focus"
import { useKeyboard } from "@react-aria/interactions"
import PropTypes from "prop-types"
import { useCallback, useEffect, useRef, useState } from "react"
import { Portal } from "react-portal"
import { RemoveScroll } from "react-remove-scroll"

import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import useAriaHidden from "helpers/use-aria-hidden"
import useHandleOutsideClick from "helpers/use-handle-outside-click"
import useWindowDimensions from "helpers/window-dimensions"
import { cn } from "styles/utils"

const getModalHeight = (height) => {
  switch (height) {
    case "fit-content":
      return "auto"
    case "full":
      return "100%"
    default:
      return height || "100%"
  }
}

const getModalAnimationClasses = (type, isVisible) => {
  switch (type) {
    case "popup":
      return isVisible ? "scale-100 opacity-100" : "scale-50 opacity-0"
    case "slideright":
      return isVisible ? "translate-x-0" : "translate-x-[100vw]"
    case "slideup":
    default:
      return isVisible ? "translate-y-0" : "translate-y-[100vh]"
  }
}

export const BackButton = ({ handleClick }) => (
  <Button
    aria-label="Back"
    className="absolute left-16 top-16 z-40"
    onClick={handleClick}
  >
    <FontAwesomeIcon className="text-grey-65 size-20" icon={faChevronLeft} />
  </Button>
)

BackButton.propTypes = {
  handleClick: PropTypes.func,
}

export const CloseButton = ({ handleClose }) => (
  <Button
    aria-label="Close"
    className="absolute right-16 top-16 z-40"
    data-cy="half-modal-close-button"
    onClick={handleClose}
  >
    <FontAwesomeIcon className="text-grey-65 size-24" icon={faTimes} />
  </Button>
)

CloseButton.propTypes = {
  handleClose: PropTypes.func,
}

const Modal = ({
  ariaLabel,
  ariaLabelledBy,
  children,
  contentClassName,
  contentIndex,
  disableClose,
  headerClassName,
  headerHTML,
  headerVariant,
  height,
  label,
  labelClassName,
  modalClassName,
  modalType,
  noHeader,
  onBack,
  onClose,
  onOutsideClick,
  shards,
  show,
  variant,
  width,
  withBackButton,
}) => {
  const [render, setRender] = useState(show)
  const [visible, setVisible] = useState(show)
  const dialogRef = useRef()
  const contentRef = useRef()
  const { isMobile } = useWindowDimensions()

  const modalHeight = isMobile ? getModalHeight(height) : "auto"

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        if (e.defaultPrevented) return

        if (typeof onClose === "function") {
          onClose()
          e.preventDefault()
        }
      }
    },
    [onClose]
  )
  const { keyboardProps } = useKeyboard({ onKeyDown: handleKeyDown })

  const handleOutsideClick = useCallback(
    (e) => {
      if (typeof onOutsideClick === "function") {
        onOutsideClick(e)
      } else if (typeof onClose === "function") {
        onClose(e)
      }
    },
    [onOutsideClick, onClose]
  )

  useAriaHidden({ enabled: render, ref: dialogRef, shards })
  useHandleOutsideClick({
    enabled: show,
    onOutsideClick: handleOutsideClick,
    ref: dialogRef,
    shards,
  })

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [contentIndex])

  useEffect(() => {
    if (document.body) {
      if (show) {
        document.body.classList.add("with-modal")
      } else {
        document.body.classList.remove("with-modal")
      }
    }
  }, [show])

  useEffect(() => {
    let timeout
    let frame

    if (show) {
      setRender(true)

      frame = requestAnimationFrame(() => {
        setVisible(true)
      })
    } else {
      setVisible(false)
      timeout = setTimeout(() => {
        setRender(false)
      }, 300)
    }

    return () => {
      clearTimeout(timeout)
      frame && cancelAnimationFrame(frame)
    }
  }, [show])

  if (
    sl.config.env !== "production" &&
    noHeader &&
    !(ariaLabel || ariaLabelledBy)
  ) {
    console.warn(
      "Modal must have an accessible name (`aria-label` or `aria-labelledby`)"
    )
  }

  return (
    render && (
      <Portal node={document?.body}>
        <div className="fixed inset-0 z-40">
          <div
            className={cn(
              "fixed inset-0 -z-10 flex items-center bg-[rgba(0,0,0,0.5)] transition-opacity duration-300",
              visible ? "opacity-100" : "opacity-0"
            )}
          />
          <RemoveScroll forwardProps>
            <div
              className={cn(
                "tablet:items-center flex h-full justify-center",
                modalType === "popup" ? "items-center" : "items-end"
              )}
            >
              <div
                {...(noHeader || ariaLabel || ariaLabelledBy
                  ? {
                      "aria-label": ariaLabel,
                      "aria-labelledby": ariaLabelledBy,
                    }
                  : {
                      "aria-labelledby": "modal_header",
                    })}
                aria-modal
                className={cn(
                  "min-w-320 tablet:max-h-[90vh] tablet:max-w-[95vw] tablet:w-auto flex max-h-full w-full flex-col overflow-hidden bg-white transition-all duration-300 ease-out",
                  getModalAnimationClasses(modalType, visible),
                  modalType === "popup" && "max-h-[90vh] w-[90vw] rounded-lg",
                  variant === "squircle" && "rounded-2xl",
                  variant === "squircle" &&
                    modalType === "slideup" &&
                    "tablet:rounded-2xl rounded-b-none rounded-t-3xl",
                  modalClassName
                )}
                data-cy="modal"
                ref={dialogRef}
                role="dialog"
                style={{ height: modalHeight }}
                {...keyboardProps}
              >
                <FocusScope
                  autoFocus={show}
                  contain={render}
                  restoreFocus={render}
                >
                  {!noHeader && (
                    <div
                      className={cn(
                        headerHTML || noHeader
                          ? "relative z-40 flex flex-auto"
                          : [
                              "relative z-40 flex flex-auto px-24",
                              headerVariant === "inline"
                                ? "justify-start"
                                : "justify-center text-center",
                              headerVariant !== "float" &&
                                `min-h-40 pt-24 ${
                                  variant === "squircle" ? "pb-10" : "pb-16"
                                }`,
                            ],
                        headerClassName
                      )}
                    >
                      {headerHTML ?? (
                        <>
                          {withBackButton && contentIndex !== 0 && (
                            <BackButton handleClick={onBack} />
                          )}
                          {!disableClose && (
                            <CloseButton handleClose={onClose} />
                          )}
                          {label && (
                            <h2
                              className={cn(
                                "font-bold leading-none text-black",
                                headerVariant === "inline"
                                  ? "text-20"
                                  : "text-24 tablet:text-28",
                                labelClassName
                              )}
                              id="modal_header"
                            >
                              {label}
                            </h2>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  <div
                    className={cn(
                      "relative w-full flex-1 overflow-y-auto px-24",
                      variant === "squircle" ? "pb-24" : "tablet:pb-32 pb-56",
                      contentClassName
                    )}
                    data-cy="modal_content"
                    ref={contentRef}
                    style={{ width: isMobile ? "100%" : width || "auto" }}
                  >
                    {children}
                  </div>
                </FocusScope>
              </div>
            </div>
          </RemoveScroll>
        </div>
      </Portal>
    )
  )
}

Modal.propTypes = {
  ariaLabel: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  children: PropTypes.node,
  contentClassName: PropTypes.string,
  contentIndex: PropTypes.number,
  disableClose: PropTypes.bool,
  headerClassName: PropTypes.string,
  headerHTML: PropTypes.node,
  headerVariant: PropTypes.oneOf(["fixed", "float", "inline"]),
  height: PropTypes.oneOfType([
    PropTypes.oneOf(["fit-content", "full"]),
    PropTypes.string,
  ]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelClassName: PropTypes.string,
  modalClassName: PropTypes.string,
  modalType: PropTypes.oneOf(["popup", "slideup", "slideright"]),
  noHeader: PropTypes.bool,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
  onOutsideClick: PropTypes.func,
  shards: PropTypes.array,
  show: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(["base", "squircle"]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  withBackButton: PropTypes.bool,
}

Modal.defaultProps = {
  contentIndex: 0,
  headerVariant: "fixed",
  height: "100%",
  modalType: "slideup",
  noHeader: false,
  variant: "base",
  withBackButton: false,
}

Modal.displayName = "Modal"

export default Modal
